/**
 * Base API client for making HTTP requests with LINE user authentication
 * Supports automatic token refresh with proactive and reactive strategies
 */

import { TokenRefreshQueue } from "@/auth/token-refresh-queue";
import { getTokenExpiry, isTokenExpired } from "@/auth/auth-storage";
import type { ApiErrorResponse } from "@/types/api-error";

// Re-export for convenience
export { isUserNotFoundError } from "@/types/api-error";
export type { ApiErrorResponse } from "@/types/api-error";

/**
 * Header types for API requests
 */
export const HeaderType = {
  DEFAULT: "DEFAULT", // reqTime + reqID
  USER_API_KEY: "USER_API_KEY", // userApiKey
  API_KEY: "API_KEY", // apiKey
  JSON_CONTENT: "JSON_CONTENT", // Content-Type: application/json
  NO_CACHE: "NO_CACHE", // Cache-Control: no-cache
  SERVER_AUTH: "SERVER_AUTH", // Authorization: Bearer {accessToken}
} as const;

export type HeaderType = (typeof HeaderType)[keyof typeof HeaderType];

interface RequestOptions extends RequestInit {
  params?: Record<string, string>;
  skipAuthRedirect?: boolean; // Skip automatic redirect on 401
  headerTypes?: HeaderType[]; // Header types to include (default: [JSON_CONTENT, NO_CACHE, SERVER_AUTH])
}

/**
 * Generate a random hex string (matches legacy hexGenerator)
 */
function hexGenerator(length: number): string {
  const chars = "0123456789abcdef";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Build headers based on specified types
 */
async function buildHeaders(
  types: HeaderType[],
  getAccessToken: (() => string | null) | null,
  apiKey?: string,
  userApiKey?: string,
): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};

  for (const type of types) {
    switch (type) {
      case HeaderType.DEFAULT:
        headers["reqTime"] = new Date().getTime().toString();
        headers["reqID"] = hexGenerator(10);
        break;
      case HeaderType.USER_API_KEY:
        if (userApiKey) {
          headers["userApiKey"] = userApiKey;
        }
        break;
      case HeaderType.API_KEY:
        if (apiKey) {
          headers["apiKey"] = apiKey;
        }
        break;
      case HeaderType.JSON_CONTENT:
        headers["Content-Type"] = "application/json";
        break;
      case HeaderType.NO_CACHE:
        headers["Cache-Control"] = "no-cache";
        break;
      case HeaderType.SERVER_AUTH:
        // Get fresh access token from auth storage each time
        if (getAccessToken) {
          const accessToken = getAccessToken();
          if (accessToken) {
            headers["Authorization"] = `Bearer ${accessToken}`;
            headers["X-Token-Type"] = "SERVER";
          }
        }
        break;
    }
  }

  return headers;
}

export class APIError extends Error {
  public statusCode: number;

  constructor(message: string, statusCode: number) {
    super(message);
    this.statusCode = statusCode;
  }
}

class APIClient {
  private baseURL: string;
  private getAccessToken: (() => string | null) | null = null;
  private apiKey: string = "";
  private userApiKey: string = "";
  private refreshQueue: TokenRefreshQueue | null = null;

  constructor(baseURL: string = "") {
    // Use baseURL from environment or empty string for proxied requests
    // Vite proxy will handle /api/* routes automatically
    this.baseURL = baseURL || "";
  }

  /**
   * Set the access token getter function
   * This function will be called for each request to get a fresh server access token
   */
  setAccessTokenGetter(getter: (() => string | null) | null) {
    this.getAccessToken = getter;
  }

  /**
   * Set the API key for authenticated requests
   */
  setApiKey(key: string) {
    this.apiKey = key;
  }

  /**
   * Set the user API key for authenticated requests
   */
  setUserApiKey(key: string) {
    this.userApiKey = key;
  }

  /**
   * Set the token refresh queue
   * The refresh function should call the refresh endpoint and update tokens in storage
   */
  setRefreshQueue(refreshFn: () => Promise<void>) {
    this.refreshQueue = new TokenRefreshQueue(refreshFn);
  }

  /**
   * Check if token needs refresh (expires within 5 minutes)
   */
  private shouldRefreshToken(): boolean {
    const expiry = getTokenExpiry();
    if (!expiry) return false;
    return isTokenExpired(expiry);
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {},
  ): Promise<T> {
    const { params, skipAuthRedirect, headerTypes, ...fetchOptions } = options;

    // Proactive refresh: check if token is expiring soon
    // Skip refresh for the /auth/token endpoint itself to avoid infinite loops
    if (this.refreshQueue && !endpoint.includes("/auth/token")) {
      if (this.shouldRefreshToken()) {
        try {
          await this.refreshQueue.refresh();
        } catch (error) {
          // If refresh fails, continue with request (might be a refresh token issue)
          // The request will fail with 401 and trigger reactive refresh or logout
          console.warn("Proactive token refresh failed:", error);
        }
      }
    }

    // Build URL with query params
    // Local dev: use /api prefix for Vite proxy (/api/* → backend)
    // Production/staging: use full baseURL directly (CORS configured on backend)
    let url: string;
    if (this.baseURL) {
      // Production/staging: direct backend URL (e.g., https://app.goodtogo.tw/auth/line)
      url = `${this.baseURL}${endpoint}`;
    } else {
      // Local dev: use /api prefix for Vite proxy (e.g., /api/auth/line)
      url = `/api${endpoint}`;
    }

    if (params) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }

    // Default header types: JSON_CONTENT, NO_CACHE, SERVER_AUTH
    // Use SERVER_AUTH (our access token) not LINE_AUTH (LINE ID token)
    const types = headerTypes || [
      HeaderType.JSON_CONTENT,
      HeaderType.NO_CACHE,
      HeaderType.SERVER_AUTH,
    ];

    // Build headers based on specified types
    const builtHeaders = await buildHeaders(
      types,
      this.getAccessToken,
      this.apiKey,
      this.userApiKey,
    );

    let response = await fetch(url, {
      ...fetchOptions,
      headers: {
        ...builtHeaders,
        ...fetchOptions.headers, // Allow override with custom headers
      },
    });

    // Reactive refresh: if 401 and refresh queue available, try refresh and retry once
    if (
      response.status === 401 &&
      this.refreshQueue &&
      !endpoint.includes("/auth/token") &&
      !skipAuthRedirect
    ) {
      // T014: Check if refresh is already in progress
      if (this.refreshQueue.isRefreshing()) {
        // Queue this request to be retried after refresh completes
        return new Promise<T>((resolve, reject) => {
          this.refreshQueue!.queueRequest({
            resolve,
            reject,
            retry: () => this.request<T>(endpoint, options),
            metadata: {
              endpoint,
              method: fetchOptions.method || "GET",
              timestamp: Date.now(),
            },
          });
        });
      }

      // Not currently refreshing - start a new refresh
      try {
        // Attempt token refresh
        await this.refreshQueue.refresh();

        // T015: Process all pending requests after successful refresh
        await this.refreshQueue.processPendingRequests();

        // Retry the original request with new token
        const refreshedHeaders = await buildHeaders(
          types,
          this.getAccessToken,
          this.apiKey,
          this.userApiKey,
        );

        response = await fetch(url, {
          ...fetchOptions,
          headers: {
            ...refreshedHeaders,
            ...fetchOptions.headers,
          },
        });
      } catch (refreshError) {
        // T015: Process pending requests with error (reject all)
        if (this.refreshQueue) {
          await this.refreshQueue.processPendingRequests(
            refreshError as Error,
          );
        }
        // Refresh failed - let the original 401 response be handled below
        console.warn("Reactive token refresh failed:", refreshError);
      }
    }

    // T016: Generic 401 → signup redirect REMOVED
    // Signup redirect now happens only in auth-service.ts for "User not found" errors
    // All other 401s will be handled by normal error handling below

    // Handle 204 No Content - return null for empty responses
    if (response.status === 204) {
      return null as T;
    }

    if (!response.ok) {
      // Parse error response body as ApiErrorResponse
      const errorText = await response.text().catch(() => "");
      let errorBody: ApiErrorResponse = {};
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

      try {
        errorBody = JSON.parse(errorText);
        // Build error message from available fields
        if (errorBody.error_description) {
          errorMessage = errorBody.error_description;
        } else if (errorBody.error) {
          errorMessage = errorBody.error;
        } else if (errorBody.message) {
          errorMessage = errorBody.message;
        }
      } catch {
        // If not JSON, use the text body if available
        if (errorText) {
          errorMessage = errorText;
        }
      }

      const error = new APIError(errorMessage, response.status);
      // Attach parsed error body for detailed error handling (T013)
      (error as APIError & { errorBody?: ApiErrorResponse }).errorBody =
        errorBody;
      throw error;
    }

    // Parse JSON response, return null for empty responses
    const text = await response.text();
    if (!text) {
      return null as T;
    }

    try {
      return JSON.parse(text);
    } catch {
      // If response is not valid JSON, return the text as-is
      return text as T;
    }
  }

  get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: "GET" });
  }

  post<T>(
    endpoint: string,
    data?: unknown,
    options?: RequestOptions,
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  put<T>(
    endpoint: string,
    data?: unknown,
    options?: RequestOptions,
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: "DELETE" });
  }
}

// Construct base URL from environment variables for production
// In development, use empty string to leverage Vite's proxy
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
const apiPath = import.meta.env.VITE_API_PATH;
const baseURL = apiBaseUrl ? `${apiBaseUrl}${apiPath || ''}` : '';

export const apiClient = import.meta.env.PROD ? new APIClient(baseURL) : new APIClient();
